import { useState } from 'react';
import { useShow, useTranslate } from '@refinedev/core';
import { DateField, Show, useTable } from '@refinedev/antd';
import {
  Button,
  Descriptions,
  Divider,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import { ScissorOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import type {
  RevenueCatBillingPeriodDetailOut,
  RevenuecatEventOut,
} from '@/api/generated/schemas';
import { ShowLink } from '@/components/common/ShowLink';
import { EntitlementLinks } from '@/components/monetization/EntitlementLinks';
import { GivePrecedenceModal } from '@/components/monetization/GivePrecedenceModal';
import { ProjectionFailureTag } from '@/components/monetization/ProjectionFailureTag';
import { RevenueCatCustomerLink } from '@/components/monetization/RevenueCatLink';
import { getSortOrder } from '@/utils/sorters';

const { Title } = Typography;
const monospace = { fontFamily: 'monospace' };

// Mirrors the backend's refusals so the trigger only shows when it can succeed.
const canTakePrecedence = (record: RevenueCatBillingPeriodDetailOut) =>
  record.entitlement_id == null &&
  record.projection_failure_reason === 'overlap_conflict' &&
  dayjs(record.valid_until).isAfter(dayjs());

export const RevenueCatBillingPeriodShow = () => {
  const t = useTranslate();
  const { query } = useShow<RevenueCatBillingPeriodDetailOut>({
    resource: 'monetization.revenuecat_billing_periods',
  });
  const { data, isLoading } = query;
  const record = data?.data;
  const [precedenceOpen, setPrecedenceOpen] = useState(false);
  const conflictingEntitlementIds = record?.conflicting_entitlement_ids ?? [];

  const { tableProps: eventsTableProps, sorters: eventsSorters } =
    useTable<RevenuecatEventOut>({
      resource: 'monetization.revenuecat_events',
      queryOptions: { enabled: Boolean(record?.transaction_id) },
      filters: {
        permanent: [
          {
            field: 'transaction_id',
            operator: 'eq',
            value: record?.transaction_id,
          },
        ],
      },
      sorters: {
        initial: [{ field: 'event_timestamp', order: 'desc' }],
      },
      pagination: { pageSize: 20 },
      syncWithLocation: false,
    });

  return (
    <Show isLoading={isLoading}>
      <Title level={4}>{t('base.sections.basic_info')}</Title>

      <Descriptions
        bordered
        column={2}
        size="small"
        style={{ marginBottom: 24 }}
      >
        <Descriptions.Item label={t('base.fields.id')}>
          {record?.id}
        </Descriptions.Item>
        <Descriptions.Item
          label={t(
            'monetization.revenuecat_billing_periods.fields.transaction_id'
          )}
        >
          {record?.transaction_id ? (
            <span style={monospace}>{record.transaction_id}</span>
          ) : (
            '-'
          )}
        </Descriptions.Item>
        <Descriptions.Item
          label={t('monetization.revenuecat_billing_periods.fields.account_id')}
        >
          {record?.account_id != null ? (
            <RevenueCatCustomerLink
              id={record.account_id}
              title={t(
                'monetization.revenuecat_billing_periods.actions.open_in_revenuecat'
              )}
            />
          ) : (
            '-'
          )}
        </Descriptions.Item>
        <Descriptions.Item
          label={t('monetization.revenuecat_billing_periods.fields.tier')}
        >
          {record?.tier ? <Tag color="blue">{record.tier}</Tag> : '-'}
        </Descriptions.Item>
        <Descriptions.Item
          label={t('monetization.revenuecat_billing_periods.fields.store')}
        >
          {record?.store ? <Tag>{record.store}</Tag> : '-'}
        </Descriptions.Item>
        <Descriptions.Item
          label={t('monetization.revenuecat_billing_periods.fields.status')}
        >
          {record?.status ? (
            <Tag color={record.status === 'active' ? 'success' : 'default'}>
              {t(
                `monetization.revenuecat_billing_periods.status.${record.status}`,
                {
                  defaultValue: record.status,
                }
              )}
            </Tag>
          ) : (
            '-'
          )}
        </Descriptions.Item>
        <Descriptions.Item
          label={t('monetization.revenuecat_billing_periods.fields.will_renew')}
        >
          {record?.will_renew != null ? (
            <Tag color={record.will_renew ? 'success' : 'default'}>
              {record.will_renew ? t('common.yes') : t('common.no')}
            </Tag>
          ) : (
            '-'
          )}
        </Descriptions.Item>
      </Descriptions>

      <Space
        align="center"
        style={{
          width: '100%',
          justifyContent: 'space-between',
          marginBottom: 16,
        }}
        wrap
      >
        <Title level={4} style={{ marginBottom: 0 }}>
          {t('monetization.revenuecat_billing_periods.sections.relationships')}
        </Title>
        {record && canTakePrecedence(record) && (
          <Button
            danger
            icon={<ScissorOutlined />}
            onClick={() => setPrecedenceOpen(true)}
          >
            {t(
              'monetization.revenuecat_billing_periods.give_precedence.trigger'
            )}
          </Button>
        )}
      </Space>

      <Descriptions
        bordered
        column={2}
        size="small"
        style={{ marginBottom: 24 }}
      >
        <Descriptions.Item
          label={t('monetization.revenuecat_billing_periods.fields.product_id')}
        >
          {record?.product_id != null ? (
            <ShowLink resource="monetization.products" id={record.product_id} />
          ) : (
            '-'
          )}
        </Descriptions.Item>
        <Descriptions.Item
          label={t(
            'monetization.revenuecat_billing_periods.fields.subscription_id'
          )}
        >
          {record?.subscription_id != null ? (
            <ShowLink
              resource="monetization.revenuecat_subscriptions"
              id={record.subscription_id}
            />
          ) : (
            '-'
          )}
        </Descriptions.Item>
        <Descriptions.Item
          label={t(
            'monetization.revenuecat_billing_periods.fields.entitlement_id'
          )}
        >
          {record?.entitlement_id != null ? (
            <ShowLink
              resource="monetization.entitlements"
              id={record.entitlement_id}
            />
          ) : (
            '-'
          )}
        </Descriptions.Item>
        <Descriptions.Item
          label={t(
            'monetization.revenuecat_billing_periods.fields.projection_failure_reason'
          )}
        >
          <ProjectionFailureTag value={record?.projection_failure_reason} />
        </Descriptions.Item>
        {conflictingEntitlementIds.length > 0 && (
          <Descriptions.Item
            label={t(
              'monetization.revenuecat_billing_periods.fields.conflicting_entitlement_ids'
            )}
            span={2}
          >
            <EntitlementLinks ids={conflictingEntitlementIds} />
          </Descriptions.Item>
        )}
      </Descriptions>

      <Title level={4}>
        {t('monetization.revenuecat_billing_periods.sections.validity')}
      </Title>

      <Descriptions bordered column={2} size="small">
        <Descriptions.Item
          label={t('monetization.revenuecat_billing_periods.fields.valid_from')}
        >
          {record?.valid_from ? (
            <DateField value={record.valid_from} format="LLL" />
          ) : (
            '-'
          )}
        </Descriptions.Item>
        <Descriptions.Item
          label={t(
            'monetization.revenuecat_billing_periods.fields.valid_until'
          )}
        >
          {record?.valid_until ? (
            <DateField value={record.valid_until} format="LLL" />
          ) : (
            '-'
          )}
        </Descriptions.Item>
      </Descriptions>

      <Divider />
      <Title level={4}>{t('base.sections.system_info')}</Title>

      <Descriptions bordered column={2} size="small">
        <Descriptions.Item
          label={t('monetization.revenuecat_billing_periods.fields.created_at')}
        >
          {record?.created_at ? (
            <DateField value={record.created_at} format="LLL" />
          ) : (
            '-'
          )}
        </Descriptions.Item>
        <Descriptions.Item
          label={t('monetization.revenuecat_billing_periods.fields.updated_at')}
        >
          {record?.updated_at ? (
            <DateField value={record.updated_at} format="LLL" />
          ) : (
            '-'
          )}
        </Descriptions.Item>
      </Descriptions>

      <Divider />
      <Title level={4}>
        {t('monetization.revenuecat_billing_periods.sections.events')}
      </Title>

      <Table<RevenuecatEventOut>
        {...eventsTableProps}
        pagination={{ ...eventsTableProps.pagination, simple: true }}
        rowKey="id"
        size="small"
      >
        <Table.Column
          dataIndex="id"
          title={t('base.fields.id')}
          sorter
          sortOrder={getSortOrder(eventsSorters, 'id')}
          render={(value: number) => (
            <ShowLink resource="monetization.revenuecat_events" id={value} />
          )}
        />
        <Table.Column
          dataIndex="event_id"
          title={t('monetization.revenuecat_events.fields.event_id')}
          sorter
          sortOrder={getSortOrder(eventsSorters, 'event_id')}
          render={(value: string) => <span style={monospace}>{value}</span>}
        />
        <Table.Column
          dataIndex="event_type"
          title={t('monetization.revenuecat_events.fields.event_type')}
          sorter
          sortOrder={getSortOrder(eventsSorters, 'event_type')}
          render={(value: string | null) => (value ? <Tag>{value}</Tag> : '-')}
        />
        <Table.Column
          dataIndex="environment"
          title={t('monetization.revenuecat_events.fields.environment')}
          sorter
          sortOrder={getSortOrder(eventsSorters, 'environment')}
          render={(value: string | null) => {
            if (!value) return '-';
            const color = value === 'PRODUCTION' ? 'success' : 'warning';
            return <Tag color={color}>{value}</Tag>;
          }}
        />
        <Table.Column
          dataIndex="store"
          title={t('monetization.revenuecat_events.fields.store')}
          sorter
          sortOrder={getSortOrder(eventsSorters, 'store')}
          render={(value: string | null) => (value ? <Tag>{value}</Tag> : '-')}
        />
        <Table.Column
          dataIndex="product_id"
          title={t('monetization.revenuecat_events.fields.product_id')}
          sorter
          sortOrder={getSortOrder(eventsSorters, 'product_id')}
          render={(value: string | null) => value ?? '-'}
        />
        <Table.Column
          dataIndex="period_type"
          title={t('monetization.revenuecat_events.fields.period_type')}
          sorter
          sortOrder={getSortOrder(eventsSorters, 'period_type')}
          render={(value: string | null) => value ?? '-'}
        />
        <Table.Column
          dataIndex="event_timestamp"
          title={t('monetization.revenuecat_events.fields.event_timestamp')}
          sorter
          sortOrder={getSortOrder(eventsSorters, 'event_timestamp')}
          render={(value: string | null) =>
            value ? <DateField value={value} format="LLL" /> : '-'
          }
        />
        <Table.Column
          dataIndex="created_at"
          title={t('monetization.revenuecat_events.fields.created_at')}
          sorter
          sortOrder={getSortOrder(eventsSorters, 'created_at')}
          render={(value: string) =>
            value ? <DateField value={value} format="LLL" /> : '-'
          }
        />
      </Table>

      {record && (
        <GivePrecedenceModal
          billingPeriodId={record.id}
          conflictingEntitlementIds={conflictingEntitlementIds}
          open={precedenceOpen}
          onClose={() => setPrecedenceOpen(false)}
        />
      )}
    </Show>
  );
};
